import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { Button, StandardDialog, TextField } from '@roc/ui';
import {
  AppraisalDetailsV2Store,
  Valuation,
} from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';

export const DISPUTE_TYPE_CLIENT = 'Client Valuation Dispute';
export const DISPUTE_TYPE_ROV = 'Internal Valuation ROV for Appraisal';
export type DisputeType = typeof DISPUTE_TYPE_CLIENT | typeof DISPUTE_TYPE_ROV;
export type DisputeCreatedResult = { disputeType: DisputeType; taskId: number; taskTitle: string };

export const ReconsiderationOfValueDialog = observer(
  ({
    store,
    open,
    onClose,
    valuation,
    isInternalUser,
    onDisputeSuccess,
  }: {
    store: AppraisalDetailsV2Store;
    open: boolean;
    onClose: () => void;
    valuation: Valuation;
    isInternalUser?: boolean;
    onDisputeSuccess?: (result: DisputeCreatedResult) => void;
  }) => {
    const formStore = store.appraisalActionsStore.appraisalActionsFormStore;
    const orderLabel = valuation?.orderNumber ?? valuation?.valuationType ?? 'this appraisal';
    const [selectedType, setSelectedType] = useState<DisputeType | null>(null);
    const [typeError, setTypeError] = useState(false);

    useEffect(() => {
      if (open) {
        store.appraisalActionsStore.reset();
        setSelectedType(null);
        setTypeError(false);
      }
    }, [open]);

    const reasonField = formStore.form.fields.reason;

    const handleConfirm = async () => {
      if (isInternalUser && !selectedType) {
        setTypeError(true);
        return;
      }
      const resolvedType: DisputeType = isInternalUser ? selectedType : DISPUTE_TYPE_CLIENT;
      const result = (await store.appraisalActionsStore.startDispute(
        valuation.clientValuationReferenceId,
        reasonField.value || '',
        resolvedType,
        !!isInternalUser
      )) as unknown as { taskId: number; taskTitle: string } | null;
      if (result) {
        onDisputeSuccess?.({ disputeType: resolvedType, taskId: result.taskId, taskTitle: result.taskTitle });
        onClose();
      }
    };

    const dialogContent = (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Please confirm you want to initiate a dispute for appraisal order{' '}
            <strong>{orderLabel}</strong>. A task will be created for you to complete and submit
            the dispute form.
          </Typography>
        </Grid>

        {isInternalUser && (
          <Grid item xs={12}>
            <FormControl error={typeError} component="fieldset" fullWidth>
              <Typography variant="subtitle2" style={{ marginBottom: 6, fontWeight: 600 }}>
                Select dispute type *
              </Typography>
              <RadioGroup
                value={selectedType ?? ''}
                onChange={e => {
                  setSelectedType(e.target.value as DisputeType);
                  setTypeError(false);
                }}
              >
                <FormControlLabel
                  value={DISPUTE_TYPE_CLIENT}
                  control={<Radio color="primary" size="small" />}
                  label="Client Valuation Dispute"
                />
                <FormControlLabel
                  value={DISPUTE_TYPE_ROV}
                  control={<Radio color="primary" size="small" />}
                  label="Internal Valuation ROV for Appraisal"
                />
              </RadioGroup>
              {typeError && (
                <FormHelperText>Please select a dispute type to continue.</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            testId="reason"
            variant="outlined"
            label="Reason for dispute"
            standaloneLabel
            fullWidth
            multiline
            minRows={4}
            value={reasonField.value || ''}
            onChange={e => formStore.onFieldChange('reason', e.target.value)}
          />
        </Grid>
      </Grid>
    );

    const dialogActions = (
      <>
        <Button
          testId="closeButton"
          variant="outlined"
          color="primary"
          onClick={onClose}
          style={{ marginRight: 8 }}
        >
          CLOSE
        </Button>
        <Button
          testId="submitButton"
          color="primary"
          variant="contained"
          onClick={handleConfirm}
        >
          CONFIRM
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        maxWidth="sm"
        title="Start Dispute"
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
      />
    );
  }
);
